import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Building, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CompanyInfo({ user, onLogout }) {
  const [config, setConfig] = useState({
    legal_name: '',
    pan_number: '',
    cin: '',
    gst_numbers: {},
    email: '',
    phone: '',
    website: '',
    pf_registration: '',
    esi_registration: '',
    pt_registration: ''
  });
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
    setHasChanges(true);
  };

  const handleGSTChange = (location, value) => {
    setConfig({ 
      ...config, 
      gst_numbers: { ...config.gst_numbers, [location]: value }
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr-config/company-info`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Company information updated successfully!');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    fetchConfig();
    setHasChanges(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Company Information</h1>
          <p className="text-gray-600 mt-2">Manage legal and statutory details</p>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">🏢 Legal & Compliance Information</h3>
          <p className="text-sm text-blue-800">
            This information appears on payslips, offer letters, and other official documents. 
            Ensure all details are accurate and up-to-date for compliance.
          </p>
        </div>

        {/* Configuration Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Legal Company Name *</label>
              <input
                type="text"
                value={config.legal_name}
                onChange={(e) => handleChange('legal_name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., Zobox Protech Limited"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">PAN Number *</label>
              <input
                type="text"
                value={config.pan_number}
                onChange={(e) => handleChange('pan_number', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="AABCZ6490M"
                maxLength="10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">CIN Number</label>
              <input
                type="text"
                value={config.cin}
                onChange={(e) => handleChange('cin', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="U52209DL2020PLC374264"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                value={config.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="hr@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Phone *</label>
              <input
                type="text"
                value={config.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="+91-9999999999"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Website</label>
              <input
                type="url"
                value={config.website}
                onChange={(e) => handleChange('website', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="https://www.company.com"
              />
            </div>
          </div>

          <h2 className="text-xl font-bold mb-4 mt-6" style={{color: '#2b3380'}}>GST Numbers</h2>
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2">Noida Branch GST</label>
              <input
                type="text"
                value={config.gst_numbers?.noida || ''}
                onChange={(e) => handleGSTChange('noida', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="09AABCZ6490M2ZI"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Delhi Branch GST</label>
              <input
                type="text"
                value={config.gst_numbers?.delhi || ''}
                onChange={(e) => handleGSTChange('delhi', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="07AABCZ6490M1ZN"
              />
            </div>
          </div>

          <h2 className="text-xl font-bold mb-4 mt-6" style={{color: '#2b3380'}}>Statutory Registrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">PF Registration</label>
              <input
                type="text"
                value={config.pf_registration}
                onChange={(e) => handleChange('pf_registration', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="PF Number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">ESI Registration</label>
              <input
                type="text"
                value={config.esi_registration}
                onChange={(e) => handleChange('esi_registration', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="ESI Number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">PT Registration</label>
              <input
                type="text"
                value={config.pt_registration}
                onChange={(e) => handleChange('pt_registration', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="PT Number"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t">
            <button
              onClick={handleSave}
              disabled={!hasChanges || loading}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: hasChanges ? '#2b3380' : '#9e9e9e'}}
            >
              <Save className="w-5 h-5" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className="px-6 py-3 rounded-lg border font-medium"
            >
              Reset
            </button>
          </div>

          {hasChanges && (
            <p className="text-sm text-orange-600 mt-3">⚠️ You have unsaved changes</p>
          )}
        </div>

        {/* Display Current Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Current Company Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Legal Name</p>
              <p className="font-medium">{config.legal_name || 'Not set'}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">PAN</p>
              <p className="font-medium">{config.pan_number || 'Not set'}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">CIN</p>
              <p className="font-medium">{config.cin || 'Not set'}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">Email</p>
              <p className="font-medium">{config.email || 'Not set'}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CompanyInfo;